import { useLanguage } from "../../context/LanguageContext";
import styles from "./StatusPage.module.css";
import { useEffect, useRef, useState } from "react";
import { getUser } from "../../helper/http.ts";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const cpuLineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 300 },
  plugins: {
    legend: {
      position: "top" as const,
      labels: { color: "#94a3b8", font: { size: 11 }, boxWidth: 12 },
    },
    title: { display: false },
    tooltip: {
      backgroundColor: "rgba(15,23,42,0.9)",
      borderColor: "rgba(148,163,184,0.2)",
      borderWidth: 1,
    },
  },
  scales: {
    x: {
      ticks: { color: "#475569", font: { size: 10 }, maxTicksLimit: 8 },
      grid: { color: "rgba(148,163,184,0.08)" },
    },
    y: {
      beginAtZero: true,
      max: 100,
      ticks: {
        color: "#475569",
        font: { size: 10 },
        callback: (v: any) => v + "%",
      },
      grid: { color: "rgba(148,163,184,0.08)" },
    },
  },
};

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "72%",
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "rgba(15,23,42,0.9)",
      borderColor: "rgba(148,163,184,0.2)",
      borderWidth: 1,
    },
  },
};

function parsePercent(s: string) {
  return parseFloat(s.replace("%", "")) || 0;
}

function DonutCard({
  label,
  used,
  total,
  free,
  usagePercent,
  color,
}: {
  label: string;
  used: string;
  total: string;
  free: string;
  usagePercent: string;
  color: string;
}) {
  const pct = parsePercent(usagePercent);
  const data = {
    datasets: [
      {
        data: [pct, 100 - pct],
        backgroundColor: [color, "rgba(148,163,184,0.1)"],
        borderWidth: 0,
        hoverBorderWidth: 0,
      },
    ],
  };

  return (
    <div className={styles.donutCard}>
      <div className={styles.donutTitle}>{label}</div>
      <div className={styles.donutWrap}>
        <Doughnut data={data} options={doughnutOptions} />
        <div className={styles.donutCenter}>
          <span className={styles.donutPct}>{Math.round(pct)}%</span>
          <span className={styles.donutSub}>used</span>
        </div>
      </div>
      <div className={styles.donutStats}>
        <div className={styles.donutStat}>
          <span className={styles.statLabel}>Total</span>
          <span className={styles.statValue}>{total}</span>
        </div>
        <div className={styles.donutStat}>
          <span className={styles.statLabel}>Used</span>
          <span className={styles.statValue} style={{ color }}>{used}</span>
        </div>
        <div className={styles.donutStat}>
          <span className={styles.statLabel}>Free</span>
          <span className={styles.statValue}>{free}</span>
        </div>
      </div>
    </div>
  );
}

function PgActivityRow({
  row,
}: {
  row: { pid: number; duration: any; usename: string; datname: string; client_addr: string; query: string };
}) {
  const [open, setOpen] = useState(false);

  const durationStr = (() => {
    if (!row.duration || typeof row.duration !== "object") return "—";
    const { hours = 0, minutes = 0, seconds = 0 } = row.duration as any;
    if (hours) return `${hours}h ${minutes}m`;
    if (minutes) return `${minutes}m ${Math.floor(seconds || 0)}s`;
    return `${(seconds || 0).toFixed(1)}s`;
  })();

  const isLong = (() => {
    if (!row.duration || typeof row.duration !== "object") return false;
    const { hours = 0, minutes = 0, seconds = 0 } = row.duration as any;
    return hours > 0 || minutes > 0 || (seconds || 0) > 10;
  })();

  return (
    <>
      <tr
        className={styles.pgRow}
        onClick={() => setOpen((o) => !o)}
        style={{ cursor: "pointer" }}
      >
        <td className={styles.procName} style={{ color: "#94a3b8" }}>
          {row.pid}
        </td>
        <td className={styles.procName}>{row.usename}</td>
        <td className={styles.procName}>{row.datname}</td>
        <td className={styles.procName} style={{ color: "#64748b" }}>
          {row.client_addr}
        </td>
        <td>
          <span
            className={styles.metaChip}
            style={
              isLong
                ? {
                    background: "rgba(248,113,113,0.15)",
                    borderColor: "rgba(248,113,113,0.35)",
                    color: "#f87171",
                  }
                : {}
            }
          >
            {durationStr}
          </span>
        </td>
        <td className={styles.pgQueryPreview}>
          {row.query.replace(/\s+/g, " ").slice(0, 60)}…
        </td>
        <td className={styles.pgExpandIcon}>{open ? "▲" : "▼"}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className={styles.pgQueryFull}>
            <pre>{row.query.trim()}</pre>
          </td>
        </tr>
      )}
    </>
  );
}

export default function StatusPage() {
  const { t } = useLanguage();

  const [cpuData, setCpuData] = useState<{ labels: string[]; datasets: any[] }>(
    { labels: [], datasets: [] },
  );

  const [cpu, setCpu] = useState({
    cores: 0,
    loadAverage1min: "0.00",
    currentLoad: "0%",
    currentLoadUser: "0%",
    currentLoadSystem: "0%",
  });
  const [disk, setDisk] = useState({
    mount: "C:",
    total: "0 GB",
    used: "0 GB",
    free: "0 GB",
    usagePercent: "0%",
  });
  const [memory, setMemory] = useState({
    total: "0 GB",
    free: "0 GB",
    used: "0 GB",
    usagePercent: "0%",
    swapTotal: "0 B",
    swapUsed: "0 B",
  });
  const [proc, setProcess] = useState<
    { name: string; cpu: number; mem: number }[]
  >([]);
  const [pgActivity, setPgActivity] = useState<any[]>([]);
  const [slowQuery, setSlowQuery] = useState<any[]>([]);

  const cpuDataRef = useRef(cpuData);
  cpuDataRef.current = cpuData;

  function updateData() {
    getUser("/system/status").then((data: any) => {
      setCpu(data.cpu);
      setDisk(data.disk);
      setMemory(data.memory);
      setProcess(data.topProcesses);
      setPgActivity(data.pgActivity || []);
      setSlowQuery(data.slowQuery || []);

      const now = new Date().toLocaleTimeString();
      const nwCpu = JSON.parse(JSON.stringify(cpuDataRef.current));

      for (const core of data.cpu.perCore) {
        const coreId = +core.core;
        const val = parseFloat(core.load.replace("%", ""));
        const colors = [
          ["#38bdf8", "rgba(56,189,248,0.15)"],
          ["#34d399", "rgba(52,211,153,0.15)"],
          ["#fbbf24", "rgba(251,191,36,0.15)"],
          ["#f87171", "rgba(248,113,113,0.15)"],
          ["#a78bfa", "rgba(167,139,250,0.15)"],
          ["#fb923c", "rgba(251,146,60,0.15)"],
          ["#e879f9", "rgba(232,121,249,0.15)"],
          ["#4ade80", "rgba(74,222,128,0.15)"],
        ];
        const [bc, bg] = colors[(coreId - 1) % colors.length];
        if (coreId > nwCpu.datasets.length) {
          nwCpu.datasets.push({
            label: `Core ${core.core}`,
            data: [val],
            borderColor: bc,
            backgroundColor: bg,
            tension: 0.4,
            fill: false,
            pointRadius: 0,
            borderWidth: 1.5,
          });
        } else {
          nwCpu.datasets[coreId - 1].data = [
            ...nwCpu.datasets[coreId - 1].data,
            val,
          ].slice(-30);
        }
      }
      nwCpu.labels = [...nwCpu.labels, now].slice(-30);
      setCpuData(nwCpu);
    });
  }

  useEffect(() => {
    updateData();
    const id = setInterval(updateData, 3000);
    return () => clearInterval(id);
  }, []);

  const maxCpu = Math.max(...proc.map((p) => p.cpu), 1);
  const maxSlowMs = Math.max(...slowQuery.map((q) => parseFloat(q.total_ms) || 0), 1);

  const hasSwap =
    memory.swapTotal && memory.swapTotal !== "0 B" && memory.swapTotal !== "0 GB";

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("system-status") || "System Status"}</h1>
        <div className={styles.badge}>
          <span className={styles.dot} />
          Live
        </div>
      </div>

      {/* ROW 1 — CPU line chart */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>CPU Usage</div>
          <div className={styles.cpuMeta}>
            <span>{cpu.cores} cores</span>
            <span>Load avg: {cpu.loadAverage1min}</span>
            <span className={styles.metaChip}>{cpu.currentLoad}</span>
            <span className={styles.chipUser}>
              user {cpu.currentLoadUser}
            </span>
            <span className={styles.chipSystem}>
              sys {cpu.currentLoadSystem}
            </span>
          </div>
        </div>
        <div className={styles.chartRow1}>
          <Line options={cpuLineOptions} data={cpuData} />
        </div>
      </div>

      {/* ROW 2 — Disk + Memory donuts */}
      <div className={styles.row2}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>Disk</div>
            <span className={styles.mountBadge}>{disk.mount}</span>
          </div>
          <DonutCard
            label="Disk Usage"
            used={disk.used}
            total={disk.total}
            free={disk.free}
            usagePercent={disk.usagePercent}
            color="#38bdf8"
          />
        </div>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>Memory</div>
          </div>
          <div className={styles.memoryRow}>
            <DonutCard
              label="RAM Usage"
              used={memory.used}
              total={memory.total}
              free={memory.free}
              usagePercent={memory.usagePercent}
              color="#818cf8"
            />
            {hasSwap && (
              <DonutCard
                label="Swap Usage"
                used={memory.swapUsed}
                total={memory.swapTotal}
                free={
                  (() => {
                    const total = parseFloat(memory.swapTotal) || 0;
                    const used = parseFloat(memory.swapUsed) || 0;
                    return (total - used).toFixed(1) + " " + (memory.swapTotal.split(" ")[1] || "GB");
                  })()
                }
                usagePercent={
                  (() => {
                    const total = parseFloat(memory.swapTotal) || 0;
                    const used = parseFloat(memory.swapUsed) || 0;
                    return total > 0 ? ((used / total) * 100).toFixed(0) + "%" : "0%";
                  })()
                }
                color="#fb923c"
              />
            )}
          </div>
        </div>
      </div>

      {/* ROW 3 — Top Processes full width */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>Top Processes</div>
          <span className={styles.updateBadge}>live</span>
        </div>
        <table className={styles.procTable}>
          <thead>
            <tr>
              <th>Process</th>
              <th>CPU</th>
              <th>Load</th>
              <th>Memory</th>
            </tr>
          </thead>
          <tbody>
            {proc.slice(0, 10).map((p, i) => (
              <tr key={i}>
                <td className={styles.procName}>{p.name}</td>
                <td className={styles.procCpuVal}>{p.cpu.toFixed(1)}%</td>
                <td className={styles.procBarCell}>
                  <div className={styles.barTrack}>
                    <div
                      className={styles.barFill}
                      style={{
                        width: Math.min((p.cpu / maxCpu) * 100, 100) + "%",
                        background:
                          p.cpu > 20
                            ? "linear-gradient(90deg,#f87171,#ef4444)"
                            : p.cpu > 5
                              ? "linear-gradient(90deg,#fbbf24,#f59e0b)"
                              : "linear-gradient(90deg,#818cf8,#7c3aed)",
                      }}
                    />
                  </div>
                </td>
                <td className={styles.procMem}>
                  {(p.mem / 1024).toFixed(0)} MB
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ROW 4 — Slow Queries */}
      {slowQuery.length > 0 && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>Slow Queries</div>
            <span className={styles.mountBadge}>pg_stat_statements</span>
          </div>
          <div className={styles.slowList}>
            {slowQuery.slice(0, 10).map((q, i) => {
              const totalMs = parseFloat(q.total_ms) || 0;
              const avgMs = parseFloat(q.avg_ms) || 0;
              const cache = parseFloat(q.cache_hit_pct) || 0;
              const barW = Math.min((totalMs / maxSlowMs) * 100, 100);
              const isHot = avgMs > 100;
              const isCold = cache < 80;

              return (
                <div key={i} className={styles.slowItem}>
                  <div className={styles.slowHeader}>
                    <span className={styles.slowQuery}>{q.query_short}</span>
                    <div className={styles.slowChips}>
                      <span className={styles.slowChip}>
                        {q.calls} calls
                      </span>
                      <span
                        className={styles.slowChip}
                        style={
                          isHot
                            ? { color: "#f87171", borderColor: "rgba(248,113,113,0.35)", background: "rgba(248,113,113,0.1)" }
                            : {}
                        }
                      >
                        avg {avgMs.toFixed(2)}ms
                      </span>
                      <span className={styles.slowChip}>
                        max {parseFloat(q.max_ms).toFixed(2)}ms
                      </span>
                      <span
                        className={styles.slowChip}
                        style={
                          isCold
                            ? { color: "#fbbf24", borderColor: "rgba(251,191,36,0.35)", background: "rgba(251,191,36,0.1)" }
                            : { color: "#4ade80", borderColor: "rgba(74,222,128,0.35)", background: "rgba(74,222,128,0.1)" }
                        }
                      >
                        cache {cache.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className={styles.slowBarRow}>
                    <div className={styles.barTrack} style={{ flex: 1 }}>
                      <div
                        className={styles.barFill}
                        style={{
                          width: barW + "%",
                          background:
                            isHot
                              ? "linear-gradient(90deg,#f87171,#ef4444)"
                              : avgMs > 10
                                ? "linear-gradient(90deg,#fbbf24,#f59e0b)"
                                : "linear-gradient(90deg,#818cf8,#7c3aed)",
                          transition: "width 0.5s ease",
                        }}
                      />
                    </div>
                    <span className={styles.slowTotal}>
                      {totalMs.toFixed(1)}ms total
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ROW 5 — PG Activity */}
      {pgActivity.length > 0 && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>Active DB Queries</div>
            <span className={styles.updateBadge}>{pgActivity.length} active</span>
          </div>
          <table className={styles.procTable}>
            <thead>
              <tr>
                <th>PID</th>
                <th>User</th>
                <th>DB</th>
                <th>Client</th>
                <th>Duration</th>
                <th>Query</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pgActivity.map((row, i) => (
                <PgActivityRow key={i} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}